import { useState } from "react";
import { api } from "@/lib/api";
import ClaimTimeline from "@/components/ClaimTimeline";
import { Search, Loader2 } from "lucide-react";

export default function TrackClaim() {
  const [claimId, setClaimId] = useState("");
  const [mobile, setMobile] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const track = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true); setResult(null);
    try {
      const { data } = await api.get(`/claims/track/${claimId.trim()}`, { params: { mobile: mobile.trim() } });
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not find this claim");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F8FAFC] py-10" data-testid="track-claim-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-center">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#003DA5] mb-2">Public Tracker</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Track your claim
          </h1>
          <p className="text-slate-600 mt-1">Enter your Claim ID and registered mobile number.</p>
        </div>

        <form onSubmit={track} className="bg-white rounded-2xl border border-slate-200 p-6 grid sm:grid-cols-[1fr_1fr_auto] gap-3">
          <input
            value={claimId}
            onChange={(e) => setClaimId(e.target.value.toUpperCase())}
            placeholder="MIC260615A8F2"
            className="bg-white border border-slate-300 rounded-xl px-4 py-3 text-slate-900 focus:ring-2 focus:ring-[#003DA5] outline-none font-mono"
            data-testid="track-claim-id"
          />
          <input
            value={mobile}
            onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
            placeholder="Registered mobile"
            maxLength={10}
            className="bg-white border border-slate-300 rounded-xl px-4 py-3 text-slate-900 focus:ring-2 focus:ring-[#003DA5] outline-none"
            data-testid="track-mobile"
          />
          <button
            disabled={loading || !claimId || mobile.length < 10}
            className="inline-flex items-center justify-center gap-2 bg-[#003DA5] hover:bg-[#002F80] disabled:bg-slate-300 text-white rounded-xl px-5 py-3 font-semibold transition-colors"
            data-testid="track-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {loading ? "Searching" : "Track"}
          </button>
        </form>

        {error && (
          <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="track-error">
            {error}
          </div>
        )}

        {result && (
          <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="track-result">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-5 border-b border-slate-100">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Claim ID</div>
                <div className="font-mono font-bold text-xl text-slate-900">{result.claim_id}</div>
                <div className="text-xs text-slate-500 mt-1">
                  Filed {new Date(result.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </div>
              </div>
              <div>
                <span className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-[#FFF2E5] text-[#FF7A00]">
                  {result.current_status}
                </span>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-6 text-sm">
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Vehicle</div>
                <div className="font-semibold text-slate-900 mt-0.5">{result.vehicle?.make} {result.vehicle?.model}</div>
                <div className="text-slate-600">{result.vehicle?.reg_no} · {result.vehicle?.year}</div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Insurer</div>
                <div className="font-semibold text-slate-900 mt-0.5">{result.insurance}</div>
              </div>
            </div>

            <h3 className="font-heading font-bold text-lg text-slate-900 mb-4">Claim timeline</h3>
            <ClaimTimeline timeline={result.timeline} currentStatus={result.current_status} />
          </div>
        )}
      </div>
    </div>
  );
}
